import { makeClient } from './apollo';
import {
    CREATE_PROJECT,
    ADD_PROJECT_INPUTS,
    USER_APPLICATION,
    GET_CONFIGURATION,
    GET_PROJECTS,
    LINK_USER,
    GET_USER,
    UPDATE_USER,
    DELETE_USER,
    UPDATE_ADDRESS,
    CREATE_ADDRESS,
    DELETE_ADDRESS,
    SEARCH_ADDRESS,
    GET_ALL_REFERENCES,
} from './graphql';
import { formatUrl } from '../utils';

export class API {
    constructor (name, url) {
        this.url = formatUrl(url);
        this.client = makeClient(name, this.url);
    }

    /**
     * Link user to platform
     */
    async linkUser (solution, user) {
        const { data } = await this.client.mutate({
            mutation: LINK_USER,
            variables: {
                solutionId: solution,
                userId: user.id,
            },
        });

        return data.linkUserToSolution;
    }

    /**
     * Get a solution's info (configuration and type)
     */
    async getSolution (id) {
        const { data } = await this.client.query({
            query: GET_CONFIGURATION,
            variables: {
                id,
            },
        });

        const configuration = data.getSolution.configuration.values.reduce((acc, { key, value }) => ({
            ...acc,
            [key.name]: value,
        }), {});

        return {
            ...data.getSolution,
            configuration,
        };
    }

    /**
     * Get user's application
     */
    async getApplication (solutionId, user) {
        const { data } = await this.client.query({
            fetchPolicy: 'network-only',
            query: USER_APPLICATION,
            variables: {
                solutionId,
                userId: user.id,
            },
        });

        return data.userApplication;
    }

    /**
     * Get a user's projects on this platform
     */
    async getProjects (solutionId, user) {
        const { data } = await this.client.query({
            errorPolicy: 'ignore',
            fetchPolicy: 'network-only',
            query: GET_PROJECTS,
            variables: {
                solutionId,
                userId: user.id,
            },
        });
        return data.getProjects.projects;
    }

    /**
     * Create a project
     */
    async createProject ({
        solutionId, userId, orderId, inputs, uploadInformation, productId,
    }) {
        const { productReference, productTitle, productUrl } = uploadInformation;
        const productReferences = productReference ? [productReference] : [];
        const { data } = await this.client.mutate({
            mutation: CREATE_PROJECT,
            variables: {
                solutionId,
                userId,
                orderId,
                productId,
                inputs,
                productReferences,
                productTitle,
                productUrl,
            },
        });

        return data.createProject;
    }

    /**
     * Add inputs to an existing project
     */
    async addProjectInputs ({ projectId, inputs }) {
        const { data } = await this.client.mutate({
            mutation: ADD_PROJECT_INPUTS,
            variables: {
                projectId,
                inputs,
            },
        });

        return data.addProjectInputs;
    }

    /**
     * Get the currently logged in user
     */
    async getUser () {
        const { data } = await this.client.query({ query: GET_USER, fetchPolicy: 'network-only' });

        return data.getMe;
    }

    /**
     * Create new address for user
     */
    async createAddress (userId, address) {
        const { data } = await this.client.mutate({
            mutation: CREATE_ADDRESS,
            variables: {
                input: {
                    userId,
                    address,
                },
            },
        });

        return data.createAddress.address;
    }

    /**
     * Delete address by id
     */
    async deleteAddress (id) {
        const { data } = await this.client.mutate({
            mutation: DELETE_ADDRESS,
            variables: {
                input: {
                    id,
                },
            },
        });

        return data.deleteAddress;
    }

    /**
     * Edit user's address by id
     */
    async updateAddress ({ id, ...address }) {
        const { data } = await this.client.mutate({
            mutation: UPDATE_ADDRESS,
            variables: {
                input: {
                    id,
                    address,
                },
            },
        });

        return data.updateAddress.address;
    }

    /**
     * Search address by string
     */
    async searchAddress (query) {
        const { data } = await this.client.query({
            query: SEARCH_ADDRESS,
            variables: {
                query,
            },
        });

        return data.searchAddress;
    }

    /**
     * Edit user information
     */
    async updateUser (userId, user, language) {
        const { data } = await this.client.mutate({
            mutation: UPDATE_USER,
            variables: {
                userId,
                user,
                language,
            },
        });

        return data.updateUser;
    }

    /**
     * Delete user account
     */
    async deleteUser (user, language) {
        const { data } = await this.client.mutate({
            mutation: DELETE_USER,
            variables: {
                id: user.id,
                language,
            },
        });

        return data.anonymizeUser;
    }

    async trackEvent ({
        solutionId,
        event,
        utmSource,
        isAnonymous,
        session,
    }) {
        try {
            const url = new URL(`${this.url}data/collect`);

            if (isAnonymous) {
                url.searchParams.set('anonymous', 1);
            }

            await fetch(url.href, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    solution_id: solutionId,
                    utm_source: utmSource,
                    event,
                    session,
                }),
            });
        } catch (err) {
            console.error(err);
        }
    }

    async getAllReferences () {
        const { data } = await this.client.query({ query: GET_ALL_REFERENCES });

        return data.preferences;
    }
}
